import Vue from 'vue'
import Router from 'vue-router'
import i18n from './plugins/i18n'
import GeneralRoutes from './routes/general'
import PlaceRoutes from './routes/place'
import { createInstance } from './helpers/axiosEZFactory'
import { LOCALE_MAP, LOCALE_MAP_INVERSE } from './constants'
import { isNil } from 'ramda'
import { useUserStore, useTranslationStore } from './stores'

Vue.use(Router)

const router = new Router({
  base: process.env.BASE_URL,
  routes: [
    ...GeneralRoutes,
    ...PlaceRoutes,
  ],
  scrollBehavior (to, from, savedPosition) {
    if (savedPosition) {
      // handles back/forward actions
      return savedPosition
    } else {
      // ensures everything else returns to the top of the scroll
      return { x: 0, y: 0 }
    }
  },
})

// set the local variables
router.beforeEach((to, from, next) => {
  if (to.name === 'error') return next()

  // get the csrfToken from the query string
  let csrfToken = to.query.csrfToken

  if (csrfToken) {
    // load the csrfToken into local storage
    localStorage.csrfToken = csrfToken
  }

  if (localStorage.csrfToken) {
    // create the EZ axios client using the auth key
    let axiosInstance = createInstance(localStorage.csrfToken)

    // intercept all ez requests to check for 401 response
    axiosInstance.interceptors.response.use((response) => {
      return response
    }, (error) => {
      if (!isNil(error.response) && error.response.status === 401) {
        // redirect the user to the ez logout page if 401
        window.location.href = `${process.env.VUE_APP_EZ_HOST}/owner-area`
      }
      return Promise.reject(error)
    })

    Vue.prototype.$httpEZ = axiosInstance
  } else {
    // redirect the user to the ez logout page if there is no csrfToken
    window.location.href = `${process.env.VUE_APP_EZ_HOST}/owner-area`
  }

  return next()
})

// load the translations before each request
router.beforeEach((to, from, next) => {
  const translationStore = useTranslationStore()
  const userStore = useUserStore()

  // do not process on 404 page
  if (to.name === 'error') return next()

  // pass through the new locale to switch
  const newLocale = LOCALE_MAP[to.query.locale]
  let locale = 'en-GB'
  if (!isNil(newLocale) && !i18n.messages[newLocale]) {
    // if a locale has been set and there is a change in locale, and it doesn't already exist in i18n
    locale = newLocale
  } else {
    // if a locale has never been then use the default
    if (localStorage.locale && !isNil(LOCALE_MAP_INVERSE[localStorage.locale])) {
      locale = localStorage.locale
    }
  }

  // console.log("the locale is ", locale);

  // only fetch the translations when we have nothing in storage i.e. first accessing the app
  if (isNil(i18n.messages[locale])) {
    translationStore.loadTranslationByLocale({ locale })
      .then(() => {
        // set the document title
        if (to.meta.titleI18NKey) {
          document.title = i18n.t(to.meta.titleI18NKey) || 'Owner Area'
        } else {
          document.title = to.meta.title || 'Owner Area'
        }
      })
      .catch(() => {
        // could not load translation so redirect
        return next('error')
      })
  }

  // fetch the current user/roles on every request
  userStore.loadCurrentUser()
    .catch(() => {
    // if there is an error getting the user then redirect to the login page
      window.location.href = `${process.env.VUE_APP_EZ_HOST}/owner-area`
    })

  return next()
})

export default router
